import styles from './Tooltip.module.scss'
import { IClass } from '@/interfaces/general';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IPoint, ISize } from '@/interfaces/shapes';
import { offsetLeft, relativeOffsetTop } from '@/utils/layout';
import { useAppSelector } from '@/hooks/appHook';
import { getScreenSize } from '@/store/screenSize/screenSize';
import { limit } from '@/utils/math';
import { useIntl } from 'react-intl';
import { isTouchDevice } from '@/utils/device';

const MARGIN = 10
const SHOW_DELAY = 800
const FORCE_UPDATE_TOOLTIPS = 'forceUpdateTooltips'
const event = new Event(FORCE_UPDATE_TOOLTIPS)
export const forceUpdateTooltip = () => {
  window.dispatchEvent(event)
}

const getCoords = (element: HTMLElement, size: ISize, container: ISize): IPoint => {
  const offsetY = relativeOffsetTop(element) - document.documentElement.scrollTop
  let top = offsetY
  let left = offsetLeft(element) - document.documentElement.scrollLeft
  top += element.clientHeight + MARGIN
  left += element.clientWidth / 2 - size.width / 2
  left = limit({
    value: left,
    min: MARGIN,
    max: container.width - size.width - MARGIN
  })
  if (top + size.height > container.height - MARGIN) {
    top = offsetY
    top -= size.height + MARGIN
  }
  return {
    x: left,
    y: top
  }
}

let lastValue: IPoint = { x: 0, y: 0 }

const Tooltip = ({ className }: IClass) => {
  const screenSize = useAppSelector(getScreenSize)
  const [text, setText] = useState('')
  const [rendered, setRendered] = useState(false)
  const [visible, setVisible] = useState(false)
  const [allElements, setAllElements] = useState<HTMLElement[]>([])
  const [selectedElement, setSelectedElement] = useState<HTMLElement>()
  const [elementSize, setElementSize] = useState<ISize>({ width: 0, height: 0 })
  const boxRef = useRef<HTMLDivElement>(null)
  /*
   * без useRef при первой загрузке из-за оптимизации
   * реакт проскакивает некоторые условия
   */
  const selectedElementRef = useRef<HTMLElement>()
  const resizeObserver = useRef<ResizeObserver>()
  if (!resizeObserver.current) {
    resizeObserver.current = new ResizeObserver((entries) => {
      const { clientWidth: width, clientHeight: height } = entries[0].target
      setElementSize({
        width,
        height
      })
    })
  }

  const checkEqual = useCallback((newElements: HTMLElement[], oldElements: HTMLElement[]): boolean => {
    if (newElements.length !== oldElements.length) {
      return false
    }
    const newElementsSet = new Set(newElements)
    for (const element of oldElements) {
      if (!newElementsSet.has(element)) {
        return false
      }
    }
    return true
  }, []);

  const updateElements = useCallback(() => {
    const elements = document.querySelectorAll('[data-tooltip]')
    const htmlElements: HTMLElement[] = []
    elements.forEach(item => {
      if (item instanceof HTMLElement) {
        htmlElements.push(item)
      }
    })
    if (checkEqual(allElements, htmlElements)) {
      return
    }
    setAllElements(htmlElements)
  }, [allElements, checkEqual]);

  updateElements()

  useEffect(() => {
    window.addEventListener(FORCE_UPDATE_TOOLTIPS, updateElements)
    return () => {
      window.removeEventListener(FORCE_UPDATE_TOOLTIPS, updateElements)
    }
  }, [updateElements]);

  const { formatMessage } = useIntl()

  useEffect(() => {
    const box = boxRef.current
    const observer = resizeObserver.current
    if (!box || !observer) {
      return
    }
    observer.observe(box)
    return () => {
      observer.unobserve(box)
    }
  }, []);

  const coords = useMemo<IPoint>(() => {
    if (!selectedElement) {
      return lastValue
    }
    lastValue = getCoords(
      selectedElement,
      elementSize,
      screenSize
    )
    return lastValue
  }, [selectedElement, screenSize, elementSize]);

  useEffect(() => {
    setRendered(!!selectedElement)
  }, [selectedElement]);

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (rendered) {
      timeout = setTimeout(() => {
        setVisible(true)
      }, SHOW_DELAY)
    } else {
      setVisible(false)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [rendered]);

  const onMouseEnter = useCallback((event: MouseEvent) => {
    const element = allElements.find(item => item === event.target)
    if (!element) {
      return
    }
    setSelectedElement(element)
    selectedElementRef.current = element
    const text = element.dataset.tooltip
    setText( text ? formatMessage({ id: text, defaultMessage: text }) : '')
  }, [allElements, formatMessage]);

  const onMouseLeave = useCallback((event: MouseEvent) => {
    if (event.type === 'mouseleave' && (!event.currentTarget || event.currentTarget !== selectedElementRef.current)) {
      return
    }
    setSelectedElement(undefined)
    selectedElementRef.current = undefined
  }, [])

  useEffect(() => {
    if (isTouchDevice()) {
      return
    }
    allElements.forEach(item => {
      item.addEventListener('mouseenter', onMouseEnter)
    })
    return () => {
      allElements.forEach(item => {
        item.removeEventListener('mouseenter', onMouseEnter)
      })
    }
  }, [allElements, onMouseEnter]);

  useEffect(() => {
    if (isTouchDevice()) {
      return
    }
    allElements.forEach(item => {
      item.addEventListener('mouseleave', onMouseLeave)
      item.addEventListener('click', onMouseLeave)
    })
    return () => {
      allElements.forEach(item => {
        item.removeEventListener('mouseleave', onMouseLeave)
        item.removeEventListener('click', onMouseLeave)
      })
    }
  }, [allElements, onMouseLeave]);

  useEffect(() => {
    if (!selectedElement) {
      return
    }
    const element = allElements.find(item => item === selectedElement)
    if (!element) {
      setSelectedElement(undefined)
      selectedElementRef.current = undefined
    }
  }, [selectedElement, allElements]);

  return <div
    ref={boxRef}
    className={classNames(
      styles.box,
      className,
      !rendered && 'd-none',
      visible && styles.visible
    )}
    style={{
      top: coords.y,
      left: coords.x
    }}
  >{text}</div>
}

export default Tooltip
